// src/features/wbs/WbsListContainer.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toProjectDateOnly, toISODate, activeWeekAnchorOf } from '../../utils/date';
import { listFlags } from '../../lib/wbs/progressService';
import type { WeekFlag, WbsFlatItem } from './types';
import { buildRollupRows } from './lib/progressRollup';
import type { WbsItemVersion } from './types';
import { listVersions, getListByVersion, apiGetApprovedMap, apiGetWeeklyProgressComparison } from './api';
import { STATUS_LABEL_KO, type WbsStatusKind } from './components/common/wbsStatus';
import { WbsFilterBar } from './components/WbsFilterBar';
import type { WbsFilters, WbsFilterOptions, WeeklyFilterKey } from './components/WbsFilterBar';
import { statusFromRow } from './components/common/statusFromRow';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';
import { useWbsCurrent } from './repo';
import { WbsViewAs, type WbsEvViewMode } from './components/overview/AsOfSelector';
import { WbsProgressEntryList } from './WbsProgressEntryList';

const SOON_DAYS = 7;

function isWithinDays(iso: string | null | undefined, base: Date, days: number) {
    if (!iso) return false;
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return false;
    const diffDays = (d.getTime() - base.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= days;
}

// 트리 인덱스(필터용)
function buildTreeIndexForFilter(rows: WbsFlatItem[]) {
    const parentMap: Record<string, string | null> = {};
    const childrenMap: Record<string, string[]> = {};
    for (const r of rows) {
        parentMap[r.id] = r.parentId ?? null;
        if (r.parentId) {
            if (!childrenMap[r.parentId]) childrenMap[r.parentId] = [];
            childrenMap[r.parentId].push(r.id);
        }
        if (!childrenMap[r.id]) childrenMap[r.id] = childrenMap[r.id] ?? [];
    }
    return { parentMap, childrenMap };
}

function filterKeepHierarchy(
    rows: WbsFlatItem[],
    idx: ReturnType<typeof buildTreeIndexForFilter>,
    predicate: (r: WbsFlatItem) => boolean,
) {
    const keep = new Set<string>();

    // match
    for (const r of rows) {
        if (predicate(r)) keep.add(r.id);
    }

    // ancestors
    for (const id of Array.from(keep)) {
        let p = idx.parentMap[id];
        while (p) {
            keep.add(p);
            p = idx.parentMap[p];
        }
    }

    // orderIndex 순서 유지
    return rows.filter((r) => keep.has(r.id));
}

export default function WbsProgressEntryContainer({
    projectId,
    canEdit = false,
    actorProjectCompanyId,
    variant = 'external',
    editableOrgIds = [],
    listTitle,
    listSubtitle,
}: {
    projectId: string;
    canEdit?: boolean;
    actorProjectCompanyId?: string | null;
    variant?: 'external' | 'internal';
    editableOrgIds?: string[];
    listTitle?: string;
    listSubtitle?: string;
}) {
    // 베이스 날짜
    const { setting, isLoading: settingLoading } = useProjectSettingCtx();

    const [viewAs, setViewAs] = useState<WbsViewAs>('WEEK');
    const [baseDate, setBaseDate] = useState<Date>(new Date());
    const [weekOfISO, setWeekOfISO] = useState<string | undefined>();
    const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);
    const [rows, setRows] = useState<WbsFlatItem[]>([]);
    const [flags, setFlags] = useState<Record<string, WeekFlag>>({});
    const [approvedMap, setApprovedMap] = useState<Record<string, { approvedValue: number; sourceWeekOf: string }>>({});
    const [approvedMapW1, setApprovedMapW1] = useState<Record<string, { approvedValue: number; sourceWeekOf: string }>>(
        {},
    );
    const [approvedMapW2, setApprovedMapW2] = useState<Record<string, { approvedValue: number; sourceWeekOf: string }>>(
        {},
    );
    const [weeklyProgressComparison, setWeeklyProgressComparison] = useState<any | null>(null);
    const [versions, setVersions] = useState<
        Array<{ version: number; isCurrent: boolean; createdAt?: string; activatedAt?: string }>
    >([]);
    const [filters, setFilters] = useState<WbsFilters>(() => ({
        companyId: variant === 'external' ? actorProjectCompanyId ?? '' : '',
        statusKinds: [],
        weeklyKinds: [],
        startSoon: false,
        finishSoon: false,
        unassignedOnly: false,
        leafOnly: false,
        hasRiskOnly: false,
        hasTaskOnly: false,
    }));
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

    const projectTz = setting?.timeZone ?? 'Asia/Seoul';
    const evViewMode: WbsEvViewMode = 'LIVE';

    // const [evViewMode, setEvViewMode] = useState<WbsEvViewMode>('LIVE');
    // [STR-ID] rows/flags/counts 모두 string 키 사용
    // const [approvalOpen, setApprovalOpen] = useState(false);

    // const { data: psRes } = useProjectSetting(projectId);
    // const projectTz = psRes?.data?.timeZone ?? 'Asia/Seoul';

    const effectiveAnchorDow = useMemo(() => {
        // 기존 프론트 기본값(금요일=5)유지. 나중에 지울 것
        const current = setting?.wbsWeekAnchorDow ?? 5;
        const pending = setting?.wbsWeekAnchorDowPending ?? null;
        const from = setting?.wbsWeekAnchorEffectiveFrom ? new Date(setting.wbsWeekAnchorEffectiveFrom) : null;

        // backend/src/modules/projects/settings/schema.ts의 판단 로직과 동일
        if (pending != null && from && baseDate >= from) return pending;

        return current;
    }, [baseDate, setting]);

    const liveAnchorDate = useMemo(() => {
        if (viewAs !== 'WEEK') {
            const d = new Date(baseDate);
            d.setHours(0, 0, 0, 0);
            return d;
        }
        return activeWeekAnchorOf(baseDate, effectiveAnchorDow);
    }, [baseDate, effectiveAnchorDow, viewAs]);

    const wbsFetchAt = useMemo(() => {
        if (viewAs !== 'WEEK') return baseDate;
        return liveAnchorDate;
    }, [baseDate, viewAs, liveAnchorDate]);

    const { data, isLoading, error, refetch } = useWbsCurrent(projectId, { viewAs, at: wbsFetchAt });
    const currentVersion = data?.list?.version;

    const refFriday = useMemo(() => {
        if (viewAs === 'WEEK') {
            return liveAnchorDate;
        }
        // DATE 모드에서는 기존의 today의 금요일을 쓰지 말고 baseDate(00:00)로 맞추는 게 일관됨
        const d = new Date(baseDate);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [baseDate, liveAnchorDate, viewAs]);

    // const effectiveWeekOfISO = useMemo(() => toISODate(refFriday), [refFriday]);
    const liveWeekOfISO = useMemo(() => toISODate(liveAnchorDate), [liveAnchorDate]);
    const effectiveWeekOfISO = useMemo(() => liveWeekOfISO, [liveWeekOfISO]);

    // const currentWeekOfISO = useMemo(() => {
    //     return toISODate(weekAnchorOf(new Date(), effectiveAnchorDow));
    // }, [effectiveAnchorDow]);

    // const liveAvailable = useMemo(() => {
    //     return selectedVersion === currentVersion && displayWeekOfISO === currentWeekOfISO;
    // }, [selectedVersion, currentVersion, displayWeekOfISO, currentWeekOfISO]);

    // useEffect(() => {
    //     if (selectedVersion == null || currentVersion == null) return;

    //     if (!liveAvailable && evViewMode === 'LIVE') {
    //         setEvViewMode('CONFIRMED');
    //     }
    // }, [selectedVersion, currentVersion, liveAvailable, evViewMode]);

    useEffect(() => {
        if (viewAs === 'WEEK') {
            setWeekOfISO(effectiveWeekOfISO);
        } else {
            setWeekOfISO(undefined);
        }
    }, [effectiveWeekOfISO, viewAs]);

    // const anchorDowLabel = useMemo(() => {
    //     return viewAs === 'WEEK' ? dowLabelKoShort(effectiveAnchorDow) : undefined;
    // }, [viewAs, effectiveAnchorDow]);

    useEffect(() => {
        listVersions(projectId)
            .then(setVersions)
            .catch(() => setVersions([]));
    }, [projectId]);

    useEffect(() => {
        if (currentVersion != null) {
            setSelectedVersion(currentVersion);
        }
    }, [currentVersion]);

    // PV 계산 기준일
    const [pvRefDate, setPvRefDate] = useState<Date>(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });

    // 공용 매핑 함수: items => rows (id=versionId, nodeId 유지, parentId=부모 versionId)
    const mapItemsToRows = useCallback(
        (items: WbsItemVersion[]): WbsFlatItem[] => {
            if (!items?.length) return [];
            const nodeIdTovVersionId = new Map(items.map((i) => [i.nodeId, i.id]));
            return items.map((v) => {
                const parentVersionId = v.parentNodeId ? (nodeIdTovVersionId.get(v.parentNodeId) ?? null) : null;
                const plannedStart = toProjectDateOnly(v.plannedStart, projectTz);
                const plannedFinish = toProjectDateOnly(v.plannedFinish, projectTz);
                const actualWeight = v.weight != null ? Number(v.weight) : undefined;

                return {
                    id: v.id,
                    nodeId: v.nodeId,
                    parentId: parentVersionId,
                    level: v.level ?? 1,
                    code: v.code ?? '',
                    name: v.name ?? '',
                    actualWeight,
                    plannedStart,
                    plannedFinish,
                    duration: v.durationDays ?? undefined,
                    earnedValue: v.actualProgress != null ? Number(v.actualProgress.toFixed(1)) : null,
                    plannedValue: v.plannedProgress != null ? Number(v.plannedProgress.toFixed(1)) : null,
                    memo: undefined,
                    executer: v.responsibleName ?? (v as any).responsibleCompanyName ?? null,
                    executerId: v.responsibleId ?? (v as any).responsibleProjectCompanyId ?? null,
                    responsibleType: v.responsibleType ?? null,
                    responsibleId: v.responsibleId ?? null,
                    responsibleName: v.responsibleName ?? null,
                };
            });
        },
        [projectTz],
    );

    // 선택된 버전에 따라 rows 갱산
    useEffect(() => {
        (async () => {
            if (selectedVersion == null) return;
            if (!Number.isInteger(selectedVersion) || selectedVersion <= 0) return;

            if (selectedVersion === currentVersion) {
                setRows(mapItemsToRows((data?.items ?? []) as WbsItemVersion[]));
            } else {
                const res = await getListByVersion(projectId, selectedVersion, {
                    viewAs,
                    at: baseDate.toISOString(),
                });
                setRows(mapItemsToRows((res?.items ?? []) as WbsItemVersion[]));
            }
        })();
    }, [currentVersion, data?.items, mapItemsToRows, projectId, selectedVersion, viewAs, baseDate]);

    // 주간 상태칩 플래그
    const refreshFlags = useCallback(
        async (rowsIn: WbsFlatItem[]) => {
            // project setting 로딩/없으면 서버 호출하지 않음
            if (settingLoading || !setting) return;

            if (!rowsIn?.length) {
                setFlags({});
                return;
            }

            const nodeIds = rowsIn.map((r) => r.nodeId);
            const resByNode: Record<string, WeekFlag> = await listFlags(projectId, effectiveWeekOfISO, nodeIds);

            // UI는 versionId 키를 사용 -> nodeId -> versionId로 변환
            const nodeIdToVid = new Map(rowsIn.map((r) => [r.nodeId, r.id]));
            const byVersion: Record<string, WeekFlag> = {};
            for (const [nodeId, flag] of Object.entries(resByNode)) {
                const vid = nodeIdToVid.get(nodeId);
                if (vid) byVersion[vid] = flag;
            }
            setFlags(byVersion);
        },
        [projectId, effectiveWeekOfISO, setting, settingLoading],
    );

    useEffect(() => {
        if (!rows.length) {
            setFlags({});
            return;
        }
        refreshFlags(rows);
    }, [rows, refreshFlags]);

    useEffect(() => {
        if (settingLoading || !setting) return;
        setApprovedMap({});
        setApprovedMapW1({});
        setApprovedMapW2({});

        if (!rows.length) {
            return;
        }
        const nodeIds = rows.map((r) => r.nodeId);
        const previousWeekOfISO = toISODate(new Date(liveAnchorDate.getFullYear(), liveAnchorDate.getMonth(), liveAnchorDate.getDate() - 7));
        const twoWeeksAgoISO = toISODate(new Date(liveAnchorDate.getFullYear(), liveAnchorDate.getMonth(), liveAnchorDate.getDate() - 14));

        Promise.all([
            apiGetApprovedMap(projectId, effectiveWeekOfISO, nodeIds).catch(() => ({})),
            apiGetApprovedMap(projectId, previousWeekOfISO, nodeIds).catch(() => ({})),
            apiGetApprovedMap(projectId, twoWeeksAgoISO, nodeIds).catch(() => ({})),
        ]).then(([currentMap, w1Map, w2Map]) => {
            setApprovedMap(currentMap);
            setApprovedMapW1(w1Map);
            setApprovedMapW2(w2Map);
        });
    }, [projectId, effectiveWeekOfISO, rows, setting, settingLoading, liveAnchorDate]);

    useEffect(() => {
        if (settingLoading || !setting) return;
        if (!rows.length) return;

        setWeeklyProgressComparison(null);

        const nodeIds = rows.map((r) => r.nodeId);
        const atISO = new Date(baseDate).toISOString();

        apiGetWeeklyProgressComparison(projectId, atISO, nodeIds)
            .then(setWeeklyProgressComparison)
            .catch(() => setWeeklyProgressComparison(null));
    }, [projectId, rows, baseDate, settingLoading, setting]);

    const refreshAll = useCallback(async () => {
        if (isRefreshing) return;

        // project setting 로딩/없으면 서버 호출하지 않음
        if (settingLoading || !setting) return;

        setIsRefreshing(true);
        try {
            // 1) Current List + items 자체를 재조회
            await refetch?.();
            // 2) 버전 목록도 재조회
            try {
                const v = await listVersions(projectId);
                setVersions(v);
            } catch {
                setVersions([]);
            }

            await refreshFlags(rows);

            if (rows.length) {
                const nodeIds = rows.map((r) => r.nodeId);
                const previousWeekOfISO = toISODate(
                    new Date(liveAnchorDate.getFullYear(), liveAnchorDate.getMonth(), liveAnchorDate.getDate() - 7),
                );
                const twoWeeksAgoISO = toISODate(
                    new Date(liveAnchorDate.getFullYear(), liveAnchorDate.getMonth(), liveAnchorDate.getDate() - 14),
                );
                const [currentMap, w1Map, w2Map] = await Promise.all([
                    apiGetApprovedMap(projectId, effectiveWeekOfISO, nodeIds).catch(() => ({})),
                    apiGetApprovedMap(projectId, previousWeekOfISO, nodeIds).catch(() => ({})),
                    apiGetApprovedMap(projectId, twoWeeksAgoISO, nodeIds).catch(() => ({})),
                ]);
                setApprovedMap(currentMap);
                setApprovedMapW1(w1Map);
                setApprovedMapW2(w2Map);
            }
            setLastRefreshedAt(new Date());
        } finally {
            setIsRefreshing(false);
        }
    }, [isRefreshing, projectId, effectiveWeekOfISO, refetch, refreshFlags, rows, setting, settingLoading, liveAnchorDate]);

    const resolveLiveEarnedValue = useCallback(
        (row: WbsFlatItem): number | null => {
            const confirmed = row.earnedValue ?? null;
            const fallbackApproved = approvedMap[row.nodeId]?.approvedValue ?? confirmed ?? null;
            const previousWeekApproved = approvedMapW1[row.nodeId]?.approvedValue ?? fallbackApproved;
            const f = flags[row.id];
            const previousWeekEffective =
                f?.previousWeekStatus === 'PENDING' || f?.previousWeekStatus === 'APPROVED'
                    ? (f.previousWeekDraftEV ?? previousWeekApproved)
                    : previousWeekApproved;

            if (!f) return previousWeekApproved;

            if (f.statusForWeek === 'APPROVED') return confirmed;
            if (f.statusForWeek === 'PENDING') return f.draftEV != null ? f.draftEV : previousWeekEffective;
            if (f.statusForWeek === 'REJECTED') return previousWeekEffective;
            if (f.draftEV != null) return f.draftEV;

            return previousWeekEffective;
        },
        [approvedMap, approvedMapW1, flags],
    );

    const baseRows = useMemo(() => {
        return rows.map((r) => ({
            ...r,
            // earnedValue: resolveLiveEarnedValue(r),
            liveEarnedValue: resolveLiveEarnedValue(r),
        }));
    }, [rows, resolveLiveEarnedValue]);

    const rollupRows = useMemo(() => {
        return buildRollupRows(baseRows);
    }, [baseRows]);

    // const filterOptions: WbsFilterOptions = useMemo(() => {
    //     const companiesMap = new Map<string, string>();

    //     for (const r of rollupRows) {
    //         if (r.executerId && r.executer) {
    //             companiesMap.set(r.executerId, r.executer);
    //         }
    //     }

    //     return {
    //         companies: Array.from(companiesMap.entries()).map(([id, name]) => ({
    //             id,
    //             name,
    //         })),
    //         // statusKinds: ['NOT_STARTED', 'DELAY', 'AT_RISK', 'ON_TRACK', 'DONE'],
    //         statusKinds: [STATUS.PLANNED, STATUS.IMPENDING, STATUS.RISK, STATUS.DELAY, STATUS.ON_TRACK],
    //     };
    // }, [rollupRows]);
    const filterOptions: WbsFilterOptions = useMemo(() => {
        const companiesMap = new Map<string, string>();
        const statusSet = new Set<string>();

        for (const r of rollupRows) {
            if (r.executerId) companiesMap.set(r.executerId, r.executer ?? r.executerId);

            // const pv = plannedPercentAt(r, pvRefDate);
            const pv = r.plannedValue ?? null;
            const kind = String(statusFromRow(r, pv, pvRefDate));
            statusSet.add(kind);
        }

        return {
            companies: Array.from(companiesMap.entries()).map(([id, name]) => ({ id, name })),
            statusKinds: Array.from(statusSet.values()).sort(),
        };
    }, [rollupRows, pvRefDate]);

    const filterSummary = useMemo(() => {
        const chips: string[] = [];

        // if (filters.statusKinds.length) chips.push(`상태: ${filters.statusKinds.join(',')}`);
        if (filters.statusKinds.length) {
            chips.push(`상태: ${filters.statusKinds.map((k) => STATUS_LABEL_KO[k as WbsStatusKind] ?? k).join(',')}`);
        }
        if (filters.weeklyKinds.length) chips.push(`주간: ${filters.weeklyKinds.join(',')}`);
        if (filters.startSoon) chips.push('착수임박(7일)');
        if (filters.finishSoon) chips.push('완료임박(7일)');
        if (filters.leafOnly) chips.push('WP(leaf)만');

        return chips;
    }, [filters]);

    // ===== filtered list rows (계층 유지) =====
    // 3) 필터(회사/상태/주간/임박/leaf/risk/task) + 계층 유지(ancestor 포함)까지 적용한 최종 rows
    // - WbsList에는 반드시 이 filteredRows를 전달해야 필터가 실제 화면에 반영됨
    const filteredRows = useMemo(() => {
        // 트리 인덱스는 "현재 화면 기준 rows" (승인 EV 반영된 rows)로 생성해야 일관됨
        const idx = buildTreeIndexForFilter(rollupRows);

        const hasCompany = !!filters.companyId;
        const hasStatus = filters.statusKinds.length > 0;
        const hasWeekly = filters.weeklyKinds.length > 0;
        const now = new Date();

        const weeklySet = new Set(filters.weeklyKinds);
        const statusSet = new Set(filters.statusKinds);

        return filterKeepHierarchy(rollupRows, idx, (r) => {
            // 회사
            if (filters.unassignedOnly && !!r.executerId) return false;
            if (hasCompany && r.executerId !== filters.companyId) return false;

            // 상태(kind)
            if (hasStatus) {
                // const pv = plannedPercentAt(r, pvRefDate);
                const pv = r.plannedValue ?? null;
                const kind = String(statusFromRow(r, pv, pvRefDate));
                if (!statusSet.has(kind)) return false;
            }

            // 주간 상태
            if (hasWeekly) {
                const f = (flags ?? {})[r.id];
                const status = (f?.statusForWeek ?? null) as any;
                const hasDraft = f?.draftEV != null;

                let wk: WeeklyFilterKey;
                if (!hasDraft && !status) wk = 'NO_INPUT';
                else if (status === 'APPROVED') wk = 'APPROVED';
                else if (status === 'PENDING') wk = 'PENDING';
                else if (status === 'REJECTED') wk = 'REJECTED';
                else wk = 'DRAFT';

                if (!weeklySet.has(wk)) return false;
            }

            // 임박
            if (filters.startSoon && !isWithinDays(r.plannedStart ?? null, now, SOON_DAYS)) return false;
            if (filters.finishSoon && !isWithinDays(r.plannedFinish ?? null, now, SOON_DAYS)) return false;

            // 확장: leafOnly / risk/task
            if (filters.leafOnly) {
                const children = idx.childrenMap[r.id] ?? [];
                if (children.length > 0) return false;
            }

            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rollupRows, filters, flags, pvRefDate]);

    const editableRowIds = useMemo(() => {
        if (!canEdit) return new Set<string>();

        const allowedOrgIds = new Set(editableOrgIds);
        const childrenMap = buildTreeIndexForFilter(rollupRows).childrenMap;

        return new Set(
            filteredRows
                .filter((row) => (childrenMap[row.id]?.length ?? 0) === 0)
                .filter((row) => {
                    if (variant === 'external') return true;
                    return (
                        row.responsibleType === 'ORG' &&
                        !!row.responsibleId &&
                        allowedOrgIds.has(row.responsibleId)
                    );
                })
                .map((row) => row.id),
        );
    }, [canEdit, editableOrgIds, filteredRows, rollupRows, variant]);

    const entryWindow = useMemo(() => {
        const closeAt = new Date(refFriday);

        closeAt.setDate(closeAt.getDate() + 1);
        closeAt.setHours(0, 0, 0, 0);

        const auto = new Date(closeAt);
        auto.setDate(auto.getDate() + 7);
        auto.setHours(0, 0, 0, 0);

        return { closeAt, autoApproveAt: auto };
    }, [refFriday, setting]);

    const entryClosed = useMemo(() => new Date() >= entryWindow.closeAt, [entryWindow]);
    const hasEditableOrgAssignment = variant !== 'internal' || editableOrgIds.length > 0;

    if (settingLoading) return <div className="mt-2 text-xs text-gray-500">Loading Project Setting…</div>;
    if (!setting) return <div className="mt-2 text-xs text-red-600">Project Setting missing</div>;

    if (isLoading) return <div className="mt-2 text-xs text-gray-500">Loading WBS…</div>;
    if (error) return <div className="mt-2 text-xs text-red-600">WBS 로딩 실패</div>;

    return (
        <div>
            {variant === 'internal' && canEdit && !hasEditableOrgAssignment && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    현재 로그인한 사용자에게 EV 입력 권한이 있는 내부 조직이 없습니다. 프로젝트 설정의 조직 멤버에서
                    해당 사용자를 조직에 추가하고 `canEvEntry`를 켜야 입력이 활성화됩니다.
                </div>
            )}
            {/* 필터 바 */}
            <WbsFilterBar
                filters={filters}
                options={filterOptions}
                onChange={setFilters}
                onResetFilters={() => {
                    setFilters({
                        companyId: variant === 'external' ? actorProjectCompanyId ?? '' : '',
                        statusKinds: [],
                        weeklyKinds: [],
                        startSoon: false,
                        finishSoon: false,
                        unassignedOnly: false,
                        leafOnly: false,
                        hasRiskOnly: false,
                        hasTaskOnly: false,
                    });
                }}
                showCompanyFilter={false}
                showUnassignedOnly={false}
                showRiskTaskFilters={false}
            />

            {/* 리스트 테이블 */}
            <WbsProgressEntryList
                projectId={projectId}
                canEdit={canEdit && selectedVersion === currentVersion}
                rows={filteredRows}
                rollupRows={rollupRows}
                pvRefDate={pvRefDate}
                refFriday={refFriday}
                flags={flags}
                onRefreshFlags={() => refreshFlags(rows)}
                onRefresh={refreshAll}
                refreshing={isRefreshing}
                lastRefreshedAt={lastRefreshedAt}
                currentListVersion={currentVersion}
                entryClosed={entryClosed}
                entryAutoApproveAt={entryWindow.autoApproveAt}
                evViewMode={evViewMode}
                editableRowIds={editableRowIds}
                weeklyProgressComparison={weeklyProgressComparison}
                approvedMapW1={approvedMapW1}
                approvedMapW2={approvedMapW2}
                filterSummary={filterSummary}
                title={listTitle}
                subtitle={listSubtitle}
                onResetFilters={() => {
                    setFilters({
                        companyId: variant === 'external' ? actorProjectCompanyId ?? '' : '',
                        statusKinds: [],
                        weeklyKinds: [],
                        startSoon: false,
                        finishSoon: false,
                        unassignedOnly: false,
                        leafOnly: false,
                        hasRiskOnly: false,
                        hasTaskOnly: false,
                    });
                }}
            />
        </div>
    );
}
